import { lazy, type ReactElement } from 'react';

export interface ServiceOperationsRouteDefinition {
  capabilityPath: string;
  label: string;
  element: ReactElement;
}

export const WorkbenchShell = lazy(() =>
  import('./pages/workbench/WorkbenchShell').then((module) => ({
    default: module.WorkbenchShell,
  }))
);

export const TicketList = lazy(() => import('./pages/service-operations/TicketsV2'));
export const TicketDetail = lazy(() => import('./pages/service-operations/TicketDetail'));
export const TicketReview = lazy(() =>
  import('./pages/service-operations/TicketReview').then((module) => ({
    default: module.TicketReview,
  }))
);
export const TicketKanban = lazy(() => import('./pages/service-operations/TicketsV2'));
export const KnownProblems = lazy(() =>
  import('./pages/service-operations/KnownProblemsPage').then((module) => ({
    default: module.KnownProblemsPage,
  }))
);
export const TicketTemplates = lazy(() =>
  import('./pages/service-operations/TicketTemplates').then((module) => ({
    default: module.TicketTemplates,
  }))
);
export const CannedResponses = lazy(() =>
  import('./pages/service-operations/CannedResponses').then((module) => ({
    default: module.CannedResponses,
  }))
);
export const WorkItemList = lazy(() => import('./pages/service-operations/WorkItemsV2'));
export const WorkItemDetail = lazy(() =>
  import('./pages/service-operations/WorkItemDetail').then((module) => ({
    default: module.WorkItemDetail,
  }))
);
export const WorkItemKanban = lazy(() =>
  import('./pages/service-operations/WorkItemKanban').then((module) => ({
    default: module.WorkItemKanban,
  }))
);
export const ProjectKanban = lazy(() =>
  import('./pages/service-operations/ProjectKanban').then((module) => ({
    default: module.ProjectKanban,
  }))
);
export const ProjectTemplates = lazy(() =>
  import('./pages/service-operations/ProjectTemplates').then((module) => ({
    default: module.ProjectTemplates,
  }))
);
export const PipelineLibrary = lazy(() =>
  import('./pages/service-operations/PipelineLibrary').then((module) => ({
    default: module.PipelineLibrary,
  }))
);
export const TimeLog = lazy(() =>
  import('./pages/service-operations/TimeLog').then((module) => ({ default: module.TimeLog }))
);
export const ReportsDashboard = lazy(() => import('./pages/service-operations/ReportsV2'));
export const OperationsHub = lazy(() =>
  import('./pages/service-operations/OperationsHub').then((module) => ({
    default: module.OperationsHub,
  }))
);
export const BillingConfig = lazy(() =>
  import('./pages/service-operations/BillingConfig').then((module) => ({
    default: module.BillingConfig,
  }))
);
export const EngineerSchedule = lazy(() =>
  import('./pages/service-operations/EngineerSchedule').then((module) => ({
    default: module.EngineerSchedule,
  }))
);
export const TimeSavingsReport = lazy(() =>
  import('./pages/service-operations/TimeSavingsReport').then((module) => ({
    default: module.TimeSavingsReport,
  }))
);
export const CallDashboard = lazy(() =>
  import('./pages/service-operations/CallDashboard').then((module) => ({
    default: module.CallDashboard,
  }))
);
export const EmailIntelligence = lazy(() =>
  import('./pages/service-operations/EmailIntelligence').then((module) => ({
    default: module.EmailIntelligence,
  }))
);
export const ApprovalInbox = lazy(() =>
  import('./pages/service-operations/ApprovalInbox').then((module) => ({
    default: module.ApprovalInbox,
  }))
);
export const TicketMerge = lazy(() =>
  import('./pages/service-operations/TicketMerge').then((module) => ({
    default: module.TicketMerge,
  }))
);

export const SLAManagement = lazy(() =>
  import('./pages/dispatch/SLAManagement').then((module) => ({
    default: module.SLAManagement,
  }))
);
export const ResolutionFeed = lazy(() =>
  import('./pages/resolve/ResolutionFeed').then((module) => ({
    default: module.ResolutionFeed,
  }))
);
export const ResolveShell = lazy(() =>
  import('./pages/resolve/ResolveShell').then((module) => ({
    default: module.ResolveShell,
  }))
);

export const SERVICE_OPERATIONS_ROUTE_DEFINITIONS: readonly ServiceOperationsRouteDefinition[] = [
  { capabilityPath: '/operations', label: 'Operations Hub', element: <OperationsHub /> },
  { capabilityPath: '/tickets', label: 'Ticket List', element: <TicketList /> },
  { capabilityPath: '/tickets/:id', label: 'Ticket Detail', element: <TicketDetail /> },
  { capabilityPath: '/tickets/:id/review', label: 'Ticket Review', element: <TicketReview /> },
  { capabilityPath: '/tickets/kanban', label: 'Ticket Kanban', element: <TicketKanban /> },
  { capabilityPath: '/tickets/merge', label: 'Ticket Merge', element: <TicketMerge /> },
  { capabilityPath: '/known-problems', label: 'Known Problems', element: <KnownProblems /> },
  { capabilityPath: '/workitems', label: 'Work Item List', element: <WorkItemList /> },
  { capabilityPath: '/workitems/:id', label: 'Work Item Detail', element: <WorkItemDetail /> },
  { capabilityPath: '/workitems/kanban', label: 'Work Item Kanban', element: <WorkItemKanban /> },
  { capabilityPath: '/projects/kanban', label: 'Project Kanban', element: <ProjectKanban /> },
  {
    capabilityPath: '/projects/:projectId/kanban',
    label: 'Project Kanban',
    element: <ProjectKanban />,
  },
  {
    capabilityPath: '/projects/templates',
    label: 'Project Templates',
    element: <ProjectTemplates />,
  },
  {
    capabilityPath: '/projects/pipelines',
    label: 'Pipeline Library',
    element: <PipelineLibrary />,
  },
  { capabilityPath: '/time/log', label: 'Time Log', element: <TimeLog /> },
  { capabilityPath: '/reports', label: 'Reports Dashboard', element: <ReportsDashboard /> },
  {
    capabilityPath: '/reports/time-savings',
    label: 'Time Savings Report',
    element: <TimeSavingsReport />,
  },
  { capabilityPath: '/calls', label: 'Call Dashboard', element: <CallDashboard /> },
  { capabilityPath: '/calls/:sessionId', label: 'Call Dashboard', element: <CallDashboard /> },
  {
    capabilityPath: '/email-intelligence',
    label: 'Email Intelligence',
    element: <EmailIntelligence />,
  },
  { capabilityPath: '/approvals', label: 'Approval Inbox', element: <ApprovalInbox /> },
  { capabilityPath: '/resolve', label: 'Resolve', element: <ResolveShell /> },
  {
    capabilityPath: '/admin/operations/ticket-templates',
    label: 'Ticket Templates',
    element: <TicketTemplates />,
  },
  {
    capabilityPath: '/admin/operations/canned-responses',
    label: 'Canned Responses',
    element: <CannedResponses />,
  },
  {
    capabilityPath: '/admin/operations/billing-config',
    label: 'Billing Config',
    element: <BillingConfig />,
  },
  {
    capabilityPath: '/admin/operations/project-templates',
    label: 'Project Templates',
    element: <ProjectTemplates />,
  },
];
